import {
  CORRESPONDENCE_MAX_ATTACHMENT_BYTES,
  correspondenceMaxSizeLabel,
} from '../config/attachmentLimits';

export const partitionFilesByMaxSize = (files, maxBytes = CORRESPONDENCE_MAX_ATTACHMENT_BYTES) => {
  const accepted = [];
  const rejected = [];
  Array.from(files || []).forEach((file) => {
    if (file.size > maxBytes) rejected.push(file);
    else accepted.push(file);
  });
  return { accepted, rejected };
};

export const correspondenceOversizeMessage = (rejected) => {
  if (!rejected?.length) return '';
  const names = rejected.map((f) => f.name).join(', ');
  return `The following file(s) exceed ${correspondenceMaxSizeLabel()}: ${names}. Please compress the file(s) and try again.`;
};

export const toStoredAttachment = (file) => new Promise((resolve) => {
  const fallback = {
    name: file.name,
    size: file.size,
    type: file.type || 'application/octet-stream',
    uploaded_at: new Date().toISOString().slice(0, 10),
  };

  const reader = new FileReader();
  reader.onload = () => {
    resolve({
      ...fallback,
      data_url: typeof reader.result === 'string' ? reader.result : null,
    });
  };
  reader.onerror = () => resolve(fallback);
  reader.readAsDataURL(file);
});

export const toStoredAttachments = async (files) => Promise.all(
  Array.from(files || []).map((file) => toStoredAttachment(file))
);

/**
 * Convert a data URL to a short blob URL. Use for iframes/new tabs to avoid
 * browser URL length limits (~2MB) that break large base64 attachments (e.g. PDFs).
 */
export const dataUrlToBlobUrl = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return null;
  try {
    const parts = dataUrl.split(',');
    if (parts.length !== 2) return null;
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    const binary = atob(parts[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

export const downloadAttachment = (file, fallbackText) => {
  if (file?.data_url) {
    const link = document.createElement('a');
    link.href = file.data_url;
    link.download = file.name || 'attachment';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  const blob = new Blob([fallbackText || 'Attachment content not available.'], {
    type: file?.type || 'application/octet-stream',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  const safeName = (file?.name || 'attachment').replace(/[\\/:*?"<>|]+/g, '_');
  link.href = url;
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Open attachment in a new browser tab for preview.
 * Uses blob URLs (not data URLs) to avoid browser URL length limits (~2MB) that break large PDFs.
 * @param {Object} file - Attachment object with data_url, name, type
 * @param {Function} [onNoDataUrl] - Called when file has no data_url; typically triggers download
 */
export const viewAttachmentInNewTab = (file, onNoDataUrl) => {
  if (!file?.data_url) {
    const fileName = (file?.name || 'attachment').replace(/"/g, '&quot;');
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(`
        <!DOCTYPE html><html><head><title>Attachment</title></head><body style="font-family:system-ui;padding:24px;max-width:480px;margin:40px auto;">
          <h2 style="color:#374151;">Preview not available</h2>
          <p style="color:#6b7280;">This attachment (${fileName}) has no preview data. Use the Download button on the previous page to save the file.</p>
        </body></html>
      `);
      w.document.close();
    }
    if (typeof onNoDataUrl === 'function') onNoDataUrl();
    return;
  }
  const blobUrl = dataUrlToBlobUrl(file.data_url);
  if (blobUrl) {
    const isImage = /^data:image\//i.test(file.data_url);
    const isPdf = /pdf/i.test(file.type || '');
    if (isImage || isPdf) {
      const w = window.open(blobUrl, '_blank');
      if (w) setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }, 10000);
      else URL.revokeObjectURL(blobUrl);
    } else {
      const safeName = (file.name || '').replace(/"/g, '&quot;');
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(`
          <!DOCTYPE html><html><head><title>${safeName}</title></head><body style="font-family:system-ui;padding:24px;max-width:480px;margin:40px auto;">
            <h2 style="color:#374151;">Preview not available</h2>
            <p style="color:#6b7280;">This file type cannot be previewed in the browser. Use the Download button to save the file.</p>
            <p><strong>File:</strong> ${safeName}</p>
          </body></html>
        `);
        w.document.close();
        URL.revokeObjectURL(blobUrl);
      } else {
        URL.revokeObjectURL(blobUrl);
      }
    }
  } else {
    if (typeof onNoDataUrl === 'function') onNoDataUrl();
  }
};
